import React, { useRef } from "react";
import {
    GridToolbarContainer,
    GridPreferencePanelsValue,
    gridPreferencePanelStateSelector,
    useGridSelector,
    gridFilterActiveItemsSelector,
    useGridApiContext,
} from "@mui/x-data-grid-pro";
import IconButton from "@mui/material/IconButton";
import FilterAltSharpIcon from "@mui/icons-material/FilterAltSharp";
import ContentCopySharpIcon from "@mui/icons-material/ContentCopySharp";
import SimCardDownloadSharpIcon from "@mui/icons-material/SimCardDownloadSharp";
import TableChartSharpIcon from "@mui/icons-material/TableChartSharp";
import Badge from "@mui/material/Badge";
import { Tooltip } from "../Tooltip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";

export type TradeStateFilter = "current" | "deleted" | "all";

interface Props {
    isOwnSet: boolean;
    onIsOwnChange: (isOwnSet: boolean) => void;
    onCopy: () => void;
    onDownload: () => void;
    copyDisabled?: boolean;
    selectedTradeStates: TradeStateFilter[];
    onTradeStatesChange: (tradeStates: TradeStateFilter[]) => void;
    setTradeStateMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
    tradeStateMenuOpen: boolean;
}

export const DataGridToolbar: React.FC<Props> = ({
    isOwnSet,
    onIsOwnChange,
    onCopy,
    onDownload,
    copyDisabled = false,
    selectedTradeStates,
    onTradeStatesChange,
    setTradeStateMenuOpen,
    tradeStateMenuOpen,
}) => {
    const apiRef = useGridApiContext();
    const { open, openedPanelValue } = useGridSelector(apiRef, gridPreferencePanelStateSelector);
    const activeFilters = useGridSelector(apiRef, gridFilterActiveItemsSelector);

    const toggleGridPreferencePanel = (panel: GridPreferencePanelsValue) => {
        if (open && openedPanelValue === panel) {
            apiRef.current.hidePreferences();
        } else {
            apiRef.current.showPreferences(panel);
        }
    };

    const optionClickRef = useRef(false);

    const isAllSelected = selectedTradeStates.includes("all");
    const isCurrent = selectedTradeStates.includes("current");
    const isDeleted = selectedTradeStates.includes("deleted");

    const toggleTradeState = (value: TradeStateFilter) => {
        let nextStates: TradeStateFilter[];

        if (value === "all") {
            nextStates = ["all"];
        } else {
            nextStates = selectedTradeStates.includes(value)
                ? selectedTradeStates.filter((item) => item !== value)
                : [...selectedTradeStates, value];
            if (nextStates.includes("all")) {
                nextStates = nextStates.filter((item) => item !== "all");
            }
            if (nextStates.length === 0) {
                nextStates = ["all"];
            }
        }

        onTradeStatesChange(nextStates);
    };

    const handleTradeStateMenuOpen = () => {
        setTradeStateMenuOpen(true);
    };

    const handleTradeStateMenuClose = () => {
        if (optionClickRef.current) {
            optionClickRef.current = false;
            return;
        }
        setTradeStateMenuOpen(false);
    };

    const renderTick = (selected: boolean) => (
        <Box sx={{ width: 20, display: "flex", justifyContent: "center" }}>
            <CheckIcon sx={{ fontSize: 18, visibility: selected ? "visible" : "hidden" }} />
        </Box>
    );

    return (
        <GridToolbarContainer>
            <Tooltip title="Filter data" arrow>
                <IconButton
                    onClick={() => toggleGridPreferencePanel(GridPreferencePanelsValue.filters)}
                    style={{ padding: 4 }}
                >
                    <Badge badgeContent={activeFilters.length} color="primary">
                        <FilterAltSharpIcon sx={{ fontSize: 28 }} />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Tooltip title="Show/hide columns" arrow>
                <IconButton
                    onClick={() => toggleGridPreferencePanel(GridPreferencePanelsValue.columns)}
                    style={{ padding: 4 }}
                >
                    <TableChartSharpIcon sx={{ fontSize: 28 }} />
                </IconButton>
            </Tooltip>
            <FormControlLabel
                control={<Switch checked={isOwnSet} onChange={(e) => onIsOwnChange(e.target.checked)} />}
                label="Own trades only"
                sx={{ marginLeft: 2 }}
            />
            <Box sx={{ display: "flex", alignItems: "center", marginLeft: 2, gap: 1.5 }}>
                <Box component="span" sx={{ whiteSpace: "nowrap", fontSize: 16 }}>
                    Trade State(s):
                </Box>
                <FormControl variant="standard" sx={{ minWidth: 130 }}>
                    <Select
                        multiple
                        value={selectedTradeStates}
                        open={tradeStateMenuOpen}
                        onOpen={handleTradeStateMenuOpen}
                        onClose={handleTradeStateMenuClose}
                        MenuProps={{ disableAutoFocusItem: true }}
                        inputProps={{ "data-testid": "trade-states-select", "aria-label": "Trade states" }}
                        renderValue={() => {
                            if (isAllSelected) {
                                return "All Trade States";
                            }
                            return selectedTradeStates.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(",");
                        }}
                    >
                        <MenuItem
                            value="all"
                            selected={isAllSelected}
                            onClick={() => {
                                toggleTradeState("all");
                                optionClickRef.current = false;
                            }}
                            onMouseDown={(event) => {
                                optionClickRef.current = true;
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                        >
                            {renderTick(isAllSelected)}
                            <ListItemText primary="All Trade States" />
                        </MenuItem>
                        <MenuItem
                            value="current"
                            selected={isCurrent}
                            onClick={() => {
                                toggleTradeState("current");
                                optionClickRef.current = false;
                            }}
                            onMouseDown={(event) => {
                                optionClickRef.current = true;
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                        >
                            {renderTick(isCurrent)}
                            <ListItemText primary="Current" />
                        </MenuItem>
                        <MenuItem
                            value="deleted"
                            selected={isDeleted}
                            onClick={() => {
                                toggleTradeState("deleted");
                                optionClickRef.current = false;
                            }}
                            onMouseDown={(event) => {
                                optionClickRef.current = true;
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                        >
                            {renderTick(isDeleted)}
                            <ListItemText primary="Deleted" />
                        </MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <div style={{ flexGrow: 1 }} />
            <Tooltip title="Copy all to clipboard" arrow>
                <span>
                    <IconButton onClick={() => onCopy()} style={{ padding: 4 }} disabled={copyDisabled}>
                        <ContentCopySharpIcon sx={{ fontSize: 25 }} />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Download CSV" arrow>
                <span>
                    <IconButton onClick={() => onDownload()} style={{ padding: 4 }} disabled={copyDisabled}>
                        <SimCardDownloadSharpIcon sx={{ fontSize: 28 }} />
                    </IconButton>
                </span>
            </Tooltip>
        </GridToolbarContainer>
    );
};
